import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudUpload, WifiOff, Trash2, Loader2, AlertTriangle, RotateCw } from "lucide-react";
import { useApi } from "../lib/api";
import { offlineNow, cachePut } from "../lib/idb";
import { listQueue, removeQueued, fixQueued, verhoeffOk, markOffline, markOnline, getVisit, syncQueue, QUEUE_EVENT, SYNC_NOW } from "../lib/offline";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

const Fix = ({ it }) => {
  const [n, setN] = useState(it.data.body.aadhaar_number || "");
  return (
    <div className="flex gap-1.5 mt-1.5">
      <input className="c-input !h-8 !py-0 text-xs font-mono" value={n} maxLength={12} onChange={(e) => setN(e.target.value.replace(/\D/g, ""))} aria-label="Aadhaar number" data-testid={`queue-fix-aadhaar-${it.id}`} />
      <Button size="sm" disabled={!verhoeffOk(n)} onClick={() => fixQueued(it, n)} className="h-8 bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] text-xs" data-testid={`queue-fix-save-${it.id}`}>Retry</Button>
    </div>);
};

const Item = ({ it }) => (
  <div className="flex items-start justify-between gap-2 py-2 border-b border-[#1F2A3E]/60 last:border-0" data-testid={`queue-item-${it.id}`}>
    <div className="min-w-0">
      <div className="text-xs text-slate-200 truncate">{it.label}</div>
      <div className="text-[10px] text-slate-500 font-mono">Saved {new Date(it.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
      {it.error && <div className="text-[11px] text-rose-400 flex gap-1 mt-0.5" data-testid={`queue-error-${it.id}`}><AlertTriangle size={11} className="mt-0.5 shrink-0" />{it.error}</div>}
      {it.error && it.kind === "worker" && <Fix it={it} />}
    </div>
    <button onClick={() => removeQueued(it.id)} aria-label="Remove" className="p-1 text-slate-500 hover:text-rose-400" data-testid={`queue-remove-${it.id}`}><Trash2 size={13} /></button>
  </div>);

export const useNet = () => {
  const [, tick] = useState(0);
  useEffect(() => {
    const f = () => tick((x) => x + 1);
    window.addEventListener("online", f); window.addEventListener("offline", f);
    return () => { window.removeEventListener("online", f); window.removeEventListener("offline", f); };
  }, []);
  return { offline: offlineNow(), stuck: navigator.onLine && offlineNow() };
};

// Once a day, saves the worker list, sites, contractors, today's attendance and worker profiles for use with no signal.
let warming = false;
const prewarm = async (api) => {
  const today = new Date().toISOString().slice(0, 10);
  if (warming || localStorage.getItem("c369-prewarm") === `${today}:2`) return;
  warming = true;
  try {
    for (const p of ["/sites", "/vendors", `/attendance?day=${today}`]) cachePut(p, (await api.get(p)).data);
    const workers = (await api.get("/workers")).data;
    cachePut("/workers", workers);
    for (const w of workers.filter((x) => x.is_active !== false).slice(0, 150)) {
      if (offlineNow()) return;
      const p = `/workers/${w.id}/profile`;
      cachePut(p, (await api.get(p)).data);
    }
    localStorage.setItem("c369-prewarm", `${today}:2`);
  } catch { /* retried on the next sync or app open */ } finally { warming = false; }
};

export const OfflineQueue = () => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const net = useNet();
  const online = !net.offline;
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => listQueue().then(setItems).catch(() => setItems([])), []);
  const run = useCallback(async (manual) => {
    if (offlineNow()) { if (manual) toast.error("Still no signal. Try again when you're back online."); return; }
    setBusy(true);
    const r = await syncQueue(api, manual);
    setBusy(false);
    if (r.ok) toast.success(`Uploaded ${r.ok} item${r.ok > 1 ? "s" : ""} from your site visit`, { description: r.labels.slice(0, 4).join(" · ") + (r.labels.length > 4 ? ` · and ${r.labels.length - 4} more` : ""), duration: 8000, position: "bottom-right" });
    if (r.clashes) toast.warning(`${r.clashes} attendance mark(s) were changed online meanwhile. The online marks were kept; review them on Attendance.`, { duration: 10000, position: "bottom-right" });
    if (r.failed) toast.error(`${r.failed} item(s) could not upload. See the reason in the list.`, { position: "bottom-right" });
    prewarm(api);
  }, [api]);
  useEffect(() => {
    load(); run(false);
    if (offlineNow()) markOffline();
    const on = () => { markOnline(); run(false); };
    const off = () => markOffline();
    const now = () => run(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    window.addEventListener(QUEUE_EVENT, load); window.addEventListener(SYNC_NOW, now);
    const t = setInterval(now, 60000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener(QUEUE_EVENT, load); window.removeEventListener(SYNC_NOW, now); clearInterval(t); };
  }, [load, run, api]);
  if (net.stuck) return (
    <button onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 rounded-full border border-[#00E599]/40 bg-[#00E599]/10 px-3 py-1 text-xs text-[#00E599]" data-testid="offline-reload-btn">
      <RotateCw size={12} />Back online · tap to reload{items.length ? ` and upload ${items.length}` : ""}</button>);
  if (online && !items.length) return null;
  const bad = items.some((i) => i.error);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${bad ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : online ? "border-sky-500/40 text-sky-300 bg-sky-500/10" : "border-amber-400/40 text-amber-300 bg-amber-400/10"}`} data-testid="offline-queue-pill">
          {busy ? <Loader2 size={12} className="animate-spin" /> : online ? <CloudUpload size={12} /> : <WifiOff size={12} />}
          {!online && <span data-testid="offline-indicator">Offline</span>}
          {items.length > 0 && <span data-testid="offline-queue-count">{items.length} waiting to upload</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#121824] border-[#1F2A3E] text-slate-100" data-testid="offline-queue-panel">
        <div className="text-sm font-semibold mb-1">Saved on this phone</div>
        {getVisit() && <p className="text-[11px] text-amber-300 mb-1" data-testid="queue-visit-info">Site visit since {new Date(getVisit().started_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {getVisit().items.length} item(s) captured. It's added to Site Visits once everything uploads.</p>}
        <p className="text-[11px] text-slate-400 mb-2">{online ? "These upload automatically. Tap Upload now to send them straight away." : "No signal. These upload automatically when you're back online."}</p>
        <div className="max-h-64 overflow-y-auto">{items.length ? items.map((it) => <Item key={it.id} it={it} />) : <p className="text-xs text-slate-500 py-3" data-testid="offline-queue-empty">Nothing waiting.</p>}</div>
        {items.length > 0 && <Button onClick={() => run(true)} disabled={busy} className="w-full mt-3 bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold" data-testid="queue-upload-now">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CloudUpload size={14} className="mr-1.5" />}Upload now</Button>}
      </PopoverContent>
    </Popover>);
};
